/**
 * Stripe card processing fee calculation.
 *
 * Rate: 1.5% + €0.25 (Stripe standard pricing for European Economic Area cards,
 * see https://stripe.com/en-de/pricing#standard-pricing).
 *
 * The tenant is charged subtotal + fee so Coliviq receives the full net rent/deposit.
 * Math is a gross-up so that `(netCents + feeCents) * (1 - rate) - fixed >= netCents`
 * holds exactly, i.e. after Stripe takes its cut we still net the net amount.
 *
 * This module is mirrored at `supabase/functions/_shared/card-fees.ts` for the
 * Deno edge function — the server is the source of truth.
 */

export const CARD_FEE_RATE_BPS = 150; // 1.5% expressed in basis points
export const CARD_FEE_FIXED_CENTS = 25;
export const CARD_FEE_BASIS = 10_000; // 100.00%

/**
 * Gross-up the processing fee so the tenant covers both the percentage *and*
 * the percentage applied to the fee itself, plus the fixed component.
 *
 * feeCents = ceil((netCents + fixed) * rate / (1 - rate)) + fixed
 */
export function computeCardFeeCents(netCents: number): number {
  if (!Number.isFinite(netCents) || netCents <= 0) return 0;
  const numerator = (netCents + CARD_FEE_FIXED_CENTS) * CARD_FEE_RATE_BPS;
  const denominator = CARD_FEE_BASIS - CARD_FEE_RATE_BPS;
  const variable = Math.ceil(numerator / denominator);
  return variable + CARD_FEE_FIXED_CENTS;
}

export function computeCardTotalCents(netCents: number): number {
  return netCents + computeCardFeeCents(netCents);
}

export type CardFeeBreakdown = {
  subtotalCents: number;
  feeCents: number;
  totalCents: number;
};

export function formatCardFeeBreakdown(netCents: number): CardFeeBreakdown {
  const feeCents = computeCardFeeCents(netCents);
  return {
    subtotalCents: netCents,
    feeCents,
    totalCents: netCents + feeCents,
  };
}

/** Human-readable rate label, e.g. "1.5% + €0.25". */
export const CARD_FEE_LABEL = "1.5% + €0.25";
